import { apiUTCToLocalDateTime, useTranslations } from "@jugl-web/utils";
import format from "date-fns/format";
import isToday from "date-fns/isToday";
import isYesterday from "date-fns/isYesterday";
import differenceInMinutes from "date-fns/differenceInMinutes";
import differenceInDays from "date-fns/differenceInDays";
import { useCallback } from "react";
import { UserStatus } from "@jugl-web/rest-api";

export const useUserStatusFormatString = () => {
  const { t } = useTranslations();

  const formatUserStatus = useCallback(
    (userStatus: UserStatus, lastSeenFallback?: Date) => {
      const { status } = userStatus;
      const emoji = userStatus.status_emoji;
      const timestamp = userStatus.last_seen_at;

      if (status === "Online" && !lastSeenFallback) {
        return t({
          id: "chats.page.online",
          defaultMessage: "Online",
        });
      }

      if (status !== "Away" && status !== "Online") {
        return `${emoji} ${status}`;
      }

      if (!timestamp) {
        return t({
          id: "chats-page.never-seen",
          defaultMessage: "Never seen",
        });
      }

      const date = lastSeenFallback ?? apiUTCToLocalDateTime(timestamp);
      const now = new Date();

      const minutesDiff = differenceInMinutes(now, date);
      const daysDiff = differenceInDays(now, date);

      if (minutesDiff < 1) {
        return t({
          id: "chats-page.last-seen-recently",
          defaultMessage: "Last seen recently",
        });
      }

      if (minutesDiff < 60) {
        return t(
          {
            id: "chats-page.last-seen-few-minutes-ago",
            defaultMessage:
              "Last seen {minutes} {minutes, plural, one {minute} other {minutes}} ago",
          },
          { minutes: minutesDiff }
        );
      }

      if (isToday(date)) {
        return t(
          {
            id: "chats-page.last-seen-today-at",
            defaultMessage: "Last seen today at {hour}",
          },
          { hour: format(date, "h:mm a") }
        );
      }

      if (isYesterday(date)) {
        return t(
          {
            id: "chats-page.last-seen-yesterday-at",
            defaultMessage: "Last seen yesterday at {hour}",
          },
          { hour: format(date, "h:mm a") }
        );
      }

      if (daysDiff <= 30) {
        return t(
          {
            id: "chats-page.last-seen-date-at-hour",
            defaultMessage: "Last seen {date} at {hour}",
          },
          { date: format(date, "MMM d"), hour: format(date, "h:mm a") }
        );
      }

      if (daysDiff <= 90) {
        return t(
          {
            id: "chats-page.last-seen-date",
            defaultMessage: "Last seen {date}",
          },
          { date: format(date, "MMM d, yyyy") }
        );
      }

      return t({
        id: "chats-page.last-seen-long-time-ago",
        defaultMessage: "Last seen a long time ago",
      });
    },
    [t]
  );

  return formatUserStatus;
};
